/**
 * advanceOnboardingSession — Sprint A.1 (Architecture Lock #5)
 * ────────────────────────────────────────────────────────────
 * SINGLE canonical lifecycle writer for OnboardingSession. Every
 * surface (wizard, ComplianceReview, admin handoff, dev tools) MUST
 * route lifecycle mutations through this function. The per-status
 * writers in `sessions.ts` remain implementation details — direct
 * callers outside `OnboardingProvider` are forbidden.
 *
 * Hard rules:
 *   • Lifecycle truth = OnboardingSession (no contractor.* writes here)
 *   • Pure derivation — returns next sessions[] + audit metadata
 *   • Defensive state-machine validation (rejects illegal transitions)
 *   • Reason ≥ MIN_REASON_LENGTH where the underlying writer demands it
 *   • Idempotent on terminal/no-op transitions
 *
 * Why a single writer:
 *   ComplianceReview previously called approveOnboarding /
 *   rejectOnboarding / requestOnboardingReupload as three distinct
 *   provider methods. That spread lifecycle responsibility across the
 *   call sites. This collapses them to one verb with a discriminated
 *   `transition` so every audit row, every guard, and every test has a
 *   single chokepoint.
 */

import {
  approveOnboardingSession,
  completeOnboardingStep,
  rejectOnboardingSession,
  requestReupload,
  submitOnboardingSession,
  updateOnboardingDraft,
} from "./sessions";
import type {
  ContractorOnboardingDraft,
  OnboardingAuditAction,
  OnboardingSession,
} from "./types";

export type OnboardingLifecycleTransition =
  | { kind: "patch_draft"; sessionId: string; patch: Partial<ContractorOnboardingDraft> }
  | { kind: "complete_step"; sessionId: string; stepKey: string; nextStep?: string }
  | { kind: "submit"; sessionId: string }
  | { kind: "request_reupload"; sessionId: string; stepKeys: string[]; reason: string }
  | { kind: "approve"; sessionId: string; reason: string }
  | { kind: "reject"; sessionId: string; reason: string };

export interface OnboardingLifecycleResult {
  sessions: OnboardingSession[];
  audit: { action: OnboardingAuditAction; sessionId: string; reason?: string; metadata?: Record<string, unknown> };
}

/**
 * Canonical lifecycle writer. Returns the next sessions[] AND the
 * audit row the caller should append. Provider wires this into its
 * single mutation queue so storage-event multi-tab handlers can
 * compare versions before flushing.
 */
export function advanceOnboardingSession(
  sessions: OnboardingSession[],
  transition: OnboardingLifecycleTransition,
  actor: string,
): OnboardingLifecycleResult {
  switch (transition.kind) {
    case "patch_draft":
      return {
        sessions: updateOnboardingDraft(sessions, transition.sessionId, transition.patch, actor),
        audit: {
          action: "draft_updated",
          sessionId: transition.sessionId,
          metadata: { fields: Object.keys(transition.patch) },
        },
      };
    case "complete_step":
      return {
        sessions: completeOnboardingStep(
          sessions,
          transition.sessionId,
          transition.stepKey,
          actor,
          transition.nextStep,
        ),
        audit: {
          action: "step_completed",
          sessionId: transition.sessionId,
          metadata: { stepKey: transition.stepKey, nextStep: transition.nextStep },
        },
      };
    case "submit":
      return {
        sessions: submitOnboardingSession(sessions, transition.sessionId, actor),
        audit: { action: "session_submitted", sessionId: transition.sessionId },
      };
    case "request_reupload":
      return {
        sessions: requestReupload(
          sessions,
          transition.sessionId,
          transition.stepKeys,
          transition.reason,
          actor,
        ),
        audit: {
          action: "reupload_requested",
          sessionId: transition.sessionId,
          reason: transition.reason,
          metadata: { stepKeys: transition.stepKeys },
        },
      };
    case "approve":
      return {
        sessions: approveOnboardingSession(sessions, transition.sessionId, actor, transition.reason),
        audit: {
          action: "session_approved",
          sessionId: transition.sessionId,
          reason: transition.reason,
        },
      };
    case "reject":
      return {
        sessions: rejectOnboardingSession(sessions, transition.sessionId, actor, transition.reason),
        audit: {
          action: "session_rejected",
          sessionId: transition.sessionId,
          reason: transition.reason,
        },
      };
  }
}
